import { useEffect, useState } from "react";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  FormControl,
  Typography,
  Theme,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ptBR from "date-fns/locale/pt-BR";
import { CalendarPickerView } from "@mui/x-date-pickers";

interface MonthYearPickerProps {
  label: string;
  minDate?: Date | null;
  maxDate?: Date | null;
  value: Date | null;
  onChange?: (date: Date | null) => void;
  hasError?: boolean;
  messageError?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  views?: string[];
}

const MonthYearPicker = ({
  label,
  minDate,
  maxDate,
  value,
  onChange,
  hasError = false,
  messageError = "",
  fullWidth = false,
  disabled = false,
  views = ["year", "month"],
}: MonthYearPickerProps) => {
  const [selectedPeriod, setSelectedPeriod] = useState<Date | null>(value);
  const [open, setOpen] = useState(false);

  const handleSelectedPeriodChange = (value: unknown) => {
    const date = value as Date | null;

    setSelectedPeriod(date);
    onChange?.(date);
  };

  useEffect(() => {
    if (value === null) {
      setSelectedPeriod(null);
    }
  }, [value]);

  useEffect(() => {
    if (disabled) {
      handleSelectedPeriodChange(null);
    }
  }, [disabled]);

  return (
    <FormControl
      sx={{
        maxWidth: 241,
        minWidth: fullWidth ? "100%" : 241,
        borderColor: "transparent",
      }}
    >
      <Typography
        sx={{
          color: hasError
            ? (theme: Theme) => theme.palette.error.light
            : "shadesOfDark.dark",
        }}
        fontFamily={"Open Sans"}
        fontWeight={700}
        mb={0.5}
      >
        {label}
      </Typography>

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
        <DatePicker
          label=""
          inputFormat="MMMM yyyy"
          views={views as CalendarPickerView[]}
          minDate={minDate}
          maxDate={maxDate}
          value={selectedPeriod}
          onChange={handleSelectedPeriodChange}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          disabled={disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                readOnly: true,
                placeholder: "mmmm aaaa",
              }}
              onClick={() => {
                if (!disabled) {
                  setOpen(true);
                }
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end">
                      <CalendarTodayIcon sx={{ color: "shadesOfDark.steel" }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: "background.paper",
                border: (theme: Theme) =>
                  hasError
                    ? `1px solid ${theme.palette.error.light}`
                    : `1px solid ${theme.palette.shadesOfDark.light}`,
                borderRadius: 1,
                padding: 0,
                "& .MuiInputBase-root": {
                  padding: "7.2px",
                  fontSize: 16,
                  fontFamily: "Open Sans",
                },
                "& .MuiInputBase-input": {
                  border: "none",
                  backgroundColor: "transparent",
                  padding: 0,
                  cursor: "pointer",
                  color: hasError
                    ? "systemColors.error.main"
                    : "shadesOfDark.dark",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
            />
          )}
        />
      </LocalizationProvider>

      {hasError && (
        <Typography
          mt={0.5}
          fontFamily={"Open Sans"}
          fontSize={10}
          color={"systemColors.error.main"}
        >
          {messageError}
        </Typography>
      )}
    </FormControl>
  );
};

export default MonthYearPicker;
